import { useRef } from 'react';
import { mergeProps, useButton, useFocusRing, useOverlayTrigger } from 'react-aria';
import { useMenuTriggerState } from 'react-stately';

import { RouteManager } from '../../../RouteManager';
import { AnimationRemain } from '../../animation/AnimationRemain';
import { Glyph } from '../../common/Glyph';
import { Overlay } from '../../overlay/Overlay';

import { useAuth } from '../../../store/AuthStore';

import { useI18n } from '../../../hooks/useI18nFormatters';
import { useUserMe } from '../../../store/users/useUserMe';

import { suPx } from '../../../theme/functions/su';
import { glyph } from '../../../theme/glyph';
import { theme } from '../../../theme/theme';
import styles from './MainNavProfileMenu.module.css';

import { MainNavProfileMenuContent } from './MainNavProfileMenuContent';

const MENU_EXIT_DURATION_MS = 200;

interface MainNavProfileMenuProps {
  /** Hides My Presence, which needs an open Homey. */
  isMinimal: boolean;
}

/**
 * Avatar button in the bottom section of the main navigation rail. Opens the profile menu beside
 * it.
 */
export function MainNavProfileMenu(props: MainNavProfileMenuProps) {
  const { i18n } = useI18n();
  const { cloudUser } = useAuth();
  const { userMe } = useUserMe();

  const triggerRef = useRef<HTMLButtonElement>(null);
  // Menu trigger state instead of plain overlay state, so it can also track the open submenu.
  const menuState = useMenuTriggerState({});
  const overlayTrigger = useOverlayTrigger({ type: 'menu' }, menuState, triggerRef);
  const button = useButton(
    {
      ...overlayTrigger.triggerProps,
      'aria-label': i18n.messageFormatter('navigation.profile.title'),
    },
    triggerRef,
  );
  const focusRing = useFocusRing();

  function handleAppearancePress() {
    RouteManager.openDialog(RouteManager.dialogState.appearanceDialog);
  }

  const avatarUrl: string | undefined = cloudUser?.avatar?.medium ?? userMe?.avatar;

  return (
    <>
      <button
        {...mergeProps(button.buttonProps, focusRing.focusProps)}
        ref={triggerRef}
        className={styles.root}
        data-is-open={menuState.isOpen}
        data-is-pressed={button.isPressed}
        data-is-focus-visible={focusRing.isFocusVisible}
      >
        {avatarUrl != null && <img className={styles.avatar} src={avatarUrl} alt="" />}

        {avatarUrl == null && (
          <Glyph
            source={glyph.core.personFill}
            size={theme.icon.size_medium}
            color={'var(--module-glyph-color)'}
          />
        )}
      </button>

      <AnimationRemain condition={menuState.isOpen} delay={MENU_EXIT_DURATION_MS}>
        {(animationRemainProps: unknown) => {
          return (
            <Overlay
              targetRef={triggerRef}
              overlayProps={overlayTrigger.overlayProps}
              overlayTriggerState={menuState}
              placement={'end bottom'}
              offset={suPx(1)}
              animationRemainProps={animationRemainProps}
              autoFocus={true}
              restoreFocus={true}
            >
              <MainNavProfileMenuContent
                isMinimal={props.isMinimal}
                menuState={menuState}
                onAppearancePress={handleAppearancePress}
              />
            </Overlay>
          );
        }}
      </AnimationRemain>
    </>
  );
}
